import { useTheme } from '@automata/mui-theme';
import { Brush as VisxBrush } from '@visx/brush';
import BaseBrush from '@visx/brush/lib/BaseBrush';
import { BrushProps } from '@visx/brush/lib/Brush';
import { PartialBrushStartEnd } from '@visx/brush/lib/types';
import { Group } from '@visx/group';
import { AnyD3Scale } from '@visx/scale';
import { useRef } from 'react';

import BrushHandle from './BrushHandle';
import { PATTERN_1_ID } from '../Pattern1';

export type Props = {
  width: number;
  height: number;
  top: number;
  scaleX: AnyD3Scale;
  scaleY: AnyD3Scale;
  onChange: BrushProps['onChange'];
};

const BrushX = ({
  width,
  height,
  top,
  scaleX,
  scaleY,
  onChange,
}: Props): JSX.Element | null => {
  const brushRef = useRef<BaseBrush | null>(null);
  const theme = useTheme();

  // initialBrushPosition will break without this check
  if (width < 10) return null;

  const handleReset = () => {
    const [domainXMin, domainXMax] = scaleX.domain();
    const [domainYMin, domainYMax] = scaleY.domain();
    onChange?.({
      x0: domainXMin,
      x1: domainXMax,
      y0: domainYMin,
      y1: domainYMax,
    });
  };

  const [, rangeXMax] = scaleX.range();

  const initialBrushPosition: PartialBrushStartEnd = {
    start: { x: 0 },
    end: { x: rangeXMax },
  };

  const padding = width - rangeXMax;

  const brushColor = theme.palette.grey[700];

  return (
    <Group left={padding / 2} top={top}>
      <VisxBrush
        innerRef={brushRef}
        width={rangeXMax}
        height={height}
        xScale={scaleX}
        yScale={scaleY}
        renderBrushHandle={(props) => (
          <BrushHandle {...props} color={brushColor} />
        )}
        onChange={onChange}
        useWindowMoveEvents
        resizeTriggerAreas={['left', 'right']}
        brushDirection="horizontal"
        initialBrushPosition={initialBrushPosition}
        selectedBoxStyle={{
          fillOpacity: 0.2,
          fill: `url(#${PATTERN_1_ID})`,
          stroke: brushColor,
          strokeWidth: 3,
          height: height - 3,
          transform: 'translate(0, 1.5)',
        }}
        onClick={handleReset}
      />
    </Group>
  );
};

export default BrushX;
